import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import axios from 'axios'
import { useUser } from '../context/UserContext';

const RecommendedOne = () => {
    const [products, setProducts] = useState([]);
    const [activeTab, setActiveTab] = useState('All');
    const [loading, setLoading] = useState(true);
    const [categoryLoading, setCategoryLoading] = useState(false);
    const { addToCart } = useUser();

    // Map tab names to API category parameters
    const getCategoryParam = (tabName) => {
        const categoryMap = {
            'Indoor': 'indoor',
            'Outdoor': 'outdoor',
            'Seeds': 'seeds',
            'Pots': 'pots'
        };
        return categoryMap[tabName];
    };

    const fetchProductsByCategory = async (tab) => {
        try {
            setCategoryLoading(true);
            let url = '/api/products?limit=6';
            
            if (tab !== 'All') {
                const categoryParam = getCategoryParam(tab);
                url = `/api/products?category=${categoryParam}&limit=6`;
            }
            
            const { data } = await axios.get(url);
            if (data.success) {
                setProducts(data.data);
            }
        } catch (error) {
            console.error(`Failed to fetch ${tab} products:`, error);
            setProducts([]);
        } finally {
            setCategoryLoading(false);
        }
    };

    // Fetch products when tab changes
    useEffect(() => {
        fetchProductsByCategory(activeTab);
    }, [activeTab]);

    // Initial load
    useEffect(() => {
        setLoading(false);
    }, []);

    const categories = ['All', 'Indoor', 'Outdoor', 'Seeds', 'Pots'];

    if (loading || categoryLoading) {
        return (
            <section className="recommended">
                <div className="container container-lg">
                    <div className="section-heading flex-between flex-wrap gap-16">
                        <h5 className="mb-0">Recommended for you</h5>
                        <ul className="nav common-tab nav-pills">
                            {categories.map((cat, idx) => (
                                <li className="nav-item" key={idx}>
                                    <button className="nav-link" type="button">
                                        {cat}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="row g-12">
                        {[1, 2, 3, 4, 5, 6].map((item) => (
                            <div key={item} className="col-lg-2 col-sm-4 col-6">
                                <div className="product-card h-100 p-8 border border-gray-100 rounded-16">
                                    <div className="skeleton-image" style={{ height: '160px', background: '#f0f0f0', borderRadius: '8px' }}></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="recommended">
            <style>{`
                .product-card:hover .product-card__badge {
                    opacity: 0;
                    visibility: hidden;
                    z-index: -1;
                    transition: all 0.3s ease;
                }
            `}</style>
            <div className="container container-lg">
                <div className="section-heading flex-between flex-wrap gap-16">
                    <h5 className="mb-0">Recommended for you</h5>
                    <ul className="nav common-tab nav-pills" id="pills-tab" role="tablist">
                        {categories.map((cat, idx) => (
                            <li className="nav-item" role="presentation" key={idx}>
                                <button
                                    className={`nav-link ${activeTab === cat ? 'active' : ''}`}
                                    onClick={() => setActiveTab(cat)}
                                    type="button"
                                    role="tab"
                                >
                                    {cat}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
                <div className="tab-content" id="pills-tabContent">
                    <div className="tab-pane fade show active" role="tabpanel">
                        {products.length === 0 ? (
                            <div className="text-center py-5 text-muted">
                                No products available in {activeTab === 'All' ? 'recommended' : activeTab} category.
                            </div>
                        ) : (
                            <div className="row g-12">
                                {products.map((product) => (
                                    <div key={product._id} className="col-lg-2 col-sm-4 col-6">
                                        <div className="product-card h-100 p-8 border border-gray-100 hover-border-main-600 rounded-16 position-relative transition-2">
                                            {product.originalPrice > product.price && (
                                                <span className="product-card__badge bg-danger-600 px-8 py-4 text-sm text-white">
                                                    Sale {product.discountPercentage || Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)}%
                                                </span>
                                            )}
                                            {product.stock <= 5 && product.stock > 0 && (
                                                <span className={`product-card__badge ₹{product.originalPrice > product.price ? 'mt-32' : ''} bg-warning-600 px-8 py-4 text-sm text-white`} style={product.originalPrice > product.price ? { top: '40px' } : {}}>
                                                    Limited {product.stock}
                                                </span>
                                            )}
                                            <Link
                                                to={`/product-details/${product._id}`}
                                                className="product-card__thumb flex-center"
                                            >
                                                <img src={product.image} alt={product.name} className="w-100" style={{ aspectRatio: '1/1', objectFit: 'cover', borderRadius: '8px' }} />
                                            </Link>
                                            <div className="product-card__content p-sm-2">
                                                <h6 className="title text-lg fw-semibold mt-12 mb-8">
                                                    <Link to={`/product-details/${product._id}`} className="link text-line-2">
                                                        {product.name}
                                                    </Link>
                                                </h6>
                                                <div className="flex-align gap-4">
                                                    <span className="text-main-600 text-md d-flex">
                                                        <i className="ph-fill ph-storefront" />
                                                    </span>
                                                    <span className="text-gray-500 text-xs">
                                                        By {product.brand || product.sellerId?.storeName || 'Paudhawala Nursery'}
                                                    </span>
                                                </div>
                                                <div className="product-card__content mt-12">
                                                    <div className="product-card__price mb-8">
                                                        <span className="text-heading text-md fw-semibold ">
                                                            ₹{product.price}{' '}
                                                        </span>
                                                        {product.originalPrice > product.price && (
                                                            <span className="text-gray-400 text-md fw-semibold text-decoration-line-through ms-1">
                                                                ₹{product.originalPrice}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="flex-align gap-6">
                                                        <span className="text-xs fw-bold text-gray-600">{product.rating || 0}</span>
                                                        <span className="text-15 fw-bold text-warning-600 d-flex">
                                                            <i className="ph-fill ph-star" />
                                                        </span>
                                                        <span className="text-xs fw-bold text-gray-600">({product.reviewCount || 0})</span>
                                                    </div>
                                                    <button
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            addToCart(product, 1);
                                                            alert(`${product.name} added to cart!`);
                                                        }}
                                                        className="product-card__cart btn bg-main-50 text-main-600 hover-bg-main-600 hover-text-white py-11 px-24 rounded-pill flex-align gap-8 mt-24 w-100 justify-content-center"
                                                        style={{ border: 'none' }}
                                                    >
                                                        Add To Cart <i className="ph ph-shopping-cart" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    )
}

export default RecommendedOne;